import RelativeTimeFormat from 'relative-time-format';
import en from 'relative-time-format/locale/en.json';

const INCREMENTS = {
    years: 31557600000,
    months: 2629800000,
    weeks: 604800000,
    days: 86400000,
    hours: 3600000,
    minutes: 60000,
    seconds: 1000,
    milliseconds: 1,
};

export const months = {
    jan: 0,
    feb: 1,
    mar: 2,
    apr: 3,
    may: 4,
    jun: 5,
    jul: 6,
    aug: 7,
    sep: 8,
    oct: 9,
    nov: 10,
    dec: 11,
};

export const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
];

export const monthChoices = monthNames.map((month, i) => {
    return { value: i, label: month };
});

export const FORMAT_OPTIONS = {
    // Date.toLocaleString(local, options)
    // mappings borrowed from momentjs
    // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat/DateTimeFormat
    ll: {
        // 2 Dec 2020
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    },
    lll: {
        // 5 Nov 2019, 2:58 pm
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour12: true,
        hour: 'numeric',
        minute: 'numeric',
    },
    llll: {
        // Thu, 19 Nov 2020, 5:07 pm
        year: 'numeric',
        month: 'short',
        weekday: 'short',
        day: 'numeric',
        hour12: true,
        hour: 'numeric',
        minute: 'numeric',
    },
    LL: {
        // 2 December 2020
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    },
    LLLL: {
        // Friday, 4 December 2020, 9:53 am
        year: 'numeric',
        month: 'long',
        weekday: 'long',
        day: 'numeric',
        hour12: true,
        hour: 'numeric',
        minute: 'numeric',
    },
    LT: {
        // 9:53 am
        hour12: true,
        hour: 'numeric',
        minute: 'numeric',
    },
    MY: {
        // March 2023
        year: 'numeric',
        month: 'long',
    },
};

export const now = () => {
    return new Date();
};

export const today = (utc = false) => {
    // return '00:00' today *in the current timezone* or in utc
    const date = now();
    if (utc) {
        date.setUTCHours(0, 0, 0, 0);
    } else {
        date.setHours(0, 0, 0, 0);
    }

    return date;
};

export const locale =
    window.navigator.userLanguage || window.navigator.language || 'en-GB';
// locale isn't set the same in all browsers and may be unnecessary.

export const DATE_ERROR = 'N/A';

// TODO: pattern for date string, Date() or new Date().toString(). i.e., Wed Jan 13 2021 13:43:08 GMT-0600 (Central Standard Time)
export const datePattern = /^\d{4}-\d{2}-\d{2}$/;
export const altDatePattern = /^\d{8}$/;
export const dateTimePattern = /^[0-9]{4}-((0[13578]|1[02])-(0[1-9]|[12][0-9]|3[01])|(0[469]|11)-(0[1-9]|[12][0-9]|30)|(02)-(0[1-9]|[12][0-9]))T(0[0-9]|1[0-9]|2[0-3]):(0[0-9]|[1-5][0-9]):(0[0-9]|[1-5][0-9])/;
const yearPattern = /^\d{4}/;
const monthPattern = /^([0-9]|1[01])$/;
const dayPattern = /^([1-9]|[12][0-9]|3[01])$/;

const makeDate = (...args) => {
    // wrapper around Date's odd behavior.
    const date = new Date(...args);

    // for ex, `new Date(`aoeuaueo`) will return a Date object...that is equal to NaN
    // this return null for an invalid date
    if (!date || isNaN(date)) {
        return null;
    }

    // TODO also fix the stupid behavior where months just roll over on you:
    // i.e.: new Date(2020, 0, 32) -->  Sat Feb 01 202

    return date;
};

export const parseDate = value => {
    /*
     * value can be one of:
     * - ISO formatted string
     * - an object with keys of year, month, day
     * - a Date object
     */

    // return null if empty (empty string for optional date field, etc)
    if (!value) {
        return null;
    }

    // if we get a Date that is a valid date, return it directly
    if (value instanceof Date && !isNaN(value)) {
        return value;
    }

    // pass in object with year/month/day keys
    // for moment compatibility
    if (typeof value === 'object') {
        const { year, month, day } = value;

        if (
            String(year).match(yearPattern) &&
            String(month).match(monthPattern) &&
            String(day).match(dayPattern)
        ) {
            // Date only requires year and month. if sending day, it must be defined.
            // time will be 0 with proper timezone.
            return makeDate(year, month, day);
        }

        return null;
    }

    let valueString = value.toString();

    // alt date pattern without dashes, add dashes
    if (valueString.match(altDatePattern)) {
        valueString = `${valueString.slice(0, 4)}-${valueString.slice(4, 6)}-${valueString.slice(6, 8)}`;
    }

    // string passed in with only date ('YYYY-MM-DD')
    if (valueString.match(datePattern)) {
        let [year, month, day] = valueString.split('-');
        month = Number(month) - 1;

        if (month < 0 || month > 11) {
            return null;
        }

        if (year === '9999') {
            // the ubiquitous no birthdate 9s
            return null;
        }

        return makeDate(year, month, day);
    }

    // string passed in full iso format with date and time
    if (valueString.match(dateTimePattern)) {
        return makeDate(value);
    }

    return null;
};

export const add = (duration, unit = 'days', date = null) => {
    date = parseDate(date) || now();

    switch (unit) {
        case 'seconds':
            date.setSeconds(date.getSeconds() + duration);
            break;
        case 'minutes':
            date.setMinutes(date.getMinutes() + duration);
            break;
        case 'hours':
            date.setHours(date.getHours() + duration);
            break;
        case 'days':
            date.setDate(date.getDate() + duration);
            break;
        case 'weeks':
            date.setDate(date.getDate() + 7 * duration);
            break;
        case 'months':
            date.setMonth(date.getMonth() + duration);
            break;
        case 'years':
            date.setFullYear(date.getFullYear() + duration);
            break;
        default:
            break;
    }

    return date;
};

export const daysInMonth = (year, month) => {
    // get number of days in month for year, month (28, 30, 31, etc)
    //
    // one month past month with zero for day and return prior month's last day
    //
    // new Date(2000, 0, 0) === Dec 31 1999
    // new Date(2000, 0, 1) === Jan 31 2000
    // new Date(2000, (0 + 1), 0) === Jan 31 2000
    return new Date(year, month + 1, 0).getDate();
};

export const diff = (date, unit = 'days', comparedDate = null) => {
    // return a Number in specified units comparing dates

    const parsedDate = parseDate(date);

    if (!parsedDate) {
        return DATE_ERROR;
    }

    let parsedCompared;
    const isDateOnly = parsedDate.toTimeString().startsWith('00:00');

    if (comparedDate) {
        parsedCompared = parseDate(comparedDate);
    } else if (isDateOnly) {
        parsedCompared = today();
    } else {
        parsedCompared = now();
    }

    const diff = Math.round((parsedDate - parsedCompared) / INCREMENTS[unit]);

    if (diff.toPrecision() === '0') {
        // test for negative zero but don't return one
        return 0;
    }

    return diff;
};

export const formatDate = (value, formatName = 'LL', locale = 'en-GB') => {
    // value is anything acceptable to parseDate, returns string for format
    const date = parseDate(value);

    if (date === null) {
        return DATE_ERROR;
    }

    if (formatName === 'YYYY-MM-DD') {
        return date.toISOString().split('T')[0];
    }

    return date.toLocaleString(locale, FORMAT_OPTIONS[formatName]);
};

export const formatRelativeDate = (value, suffix = true, increment = null) => {
    // pass in yesterday's date => '1 day ago', etc.

    // momentjs' fromNow, from, toNow, and to
    const parsedDate = parseDate(value);

    if (!parsedDate) {
        return DATE_ERROR;
    }
    // don't care about milliseconds and it just pushes
    // our comparisons off by one
    parsedDate.setMilliseconds(0);

    let compareDate = now();
    compareDate.setMilliseconds(0);

    const timeDiff = parsedDate - compareDate;

    if (!increment) {
        for (const [key, value] of Object.entries(INCREMENTS)) {
            if (Math.abs(timeDiff) / value >= 1) {
                increment = key;
                break;
            }
        }
    }

    if (!increment) {
        return 'now';
    }

    const units = Math.round(timeDiff / INCREMENTS[increment]);

    let formatted;

    RelativeTimeFormat.addLocale(en);
    const rtf = new RelativeTimeFormat('en', {
        localeMatcher: 'best fit',
        numeric: 'auto',
        style: 'long',
    });

    formatted = rtf.format(units, increment.slice(0, -1));
    // native Intl.relativetimeformat increments are plural (days vs day)

    if (!suffix) {
        formatted = formatted.replace(' ago', '').replace('in ', '');
    }

    return formatted;
};

// using as a setting in a single spot for age input
// youngest possible person == 'maximum' valid birth date
// oldest possible person == 'minimum' valid birth date
export const maxBirthDate = add(-16, 'years');
export const minBirthDate = add(-120, 'years');

const dates = {
    add,
    daysInMonth,
    diff,
    formatDate,
    FORMAT_OPTIONS,
    formatRelativeDate,
    locale,
    monthChoices,
    months,
    monthNames,
    now,
    parseDate,
    today,
};

export default dates;
